import { FormEvent, useState } from 'react';
import useTableOrder from 'hooks/tableOrder';
import { api } from 'services/api';
import { Paginated } from 'types/paginated';
import { Package } from 'types/package';
import { moneyFormat } from 'helpers/moneyFormat';

export type PackageQueryParams = {
  name: string;
  active: boolean;
};

export function useFetchPackages() {
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [rows, setRows] = useState<number>(96);
  const [total, setTotal] = useState<number>(0);
  const [orderedIndex, sort] = useTableOrder();
  const [filter, setFilter] = useState<PackageQueryParams>({
    name: '',
    active: true,
  });

  function fetchData(nextPage = page, nextRows = rows, nextFilter = filter) {
    setLoading(true);

    api
      .get<Paginated<Package[]>>('/packages', {
        params: {
          ...nextFilter,
          active: nextFilter.active ? 1 : 0,
          page: nextPage + 1,
          rows: nextRows,
        },
      })
      .then(response => {
        setTotal(response.data.total);
        setPackages(
          response.data.items.map(item => ({
            ...item,
            formattedCostPrice: moneyFormat(item.cost_price),
            formattedSalePriceMax: moneyFormat(item.sale_price_max),
            formattedSalePriceMin: moneyFormat(item.sale_price_min),
            formattedSupplierName: item.supplier?.company_name || 'N/A',
          })),
        );
      })
      .finally(() => setLoading(false));
  }

  function handleSearch(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    setPage(0);
    fetchData(0, rows, filter);
  }

  function handleChangePage(nextPage: number) {
    setPage(nextPage);
    fetchData(nextPage, rows, filter);
  }

  function handleChangeRows(nextRows: number) {
    setRows(nextRows);
    setPage(0);
    fetchData(0, nextRows, filter);
  }

  function handleSort(index: string) {
    const p = sort(index, packages);
    setPackages(p);
  }

  function handleChange(index: keyof PackageQueryParams, value: string) {
    setFilter(state => ({
      ...state,
      [index]: value,
    }));
  }

  return {
    packages,
    loading,
    orderedIndex,
    handleSort,
    filter,
    handleChange,
    setPage: handleChangePage,
    setRows: handleChangeRows,
    handleSearch,
    page,
    rows,
    total,
  };
}
